// hooks/useGptStream.js

import { useState, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchGptResponseStream, streamChatResponse, deleteConversation
} from './inferenceApi';

import {
  selectConversationById,
  clearResponse,
  clearConversationById,
  setConversationTitle,
  setActiveConversation,
  selectAllConversations,


} from '../../store/slices/gpt.slice';

/**
 *
 * State Structure
 * {
 *   responses: {}, // Keyed by conversation UUID
 *   status: 'idle' | 'loading' | 'succeeded' | 'failed',
 *   activeConversationId: string | null,
 *   conversationTitles: {}, // Keyed by conversation ID
 *   chatContext: string,
 *   selectedPortfolioId: string | null,
 *   error: string | null
 * }
 *
 * Conversation Structure
 * Each conversation in `responses` has this structure:
 * {
 *   messages: Array<{
 *     role: 'user' | 'assistant',
 *     content: string,
 *     timestamp?: string,
 *     messageId?: string
 *   }>,
 *   status: 'idle' | 'streaming' | 'complete',
 *   groupId?: string // Optional group identifier (e.g., portfolio ID)
 * }
 *
 * Streaming Implementation
 * The streaming system works through two main reducers:
 *
 * 1. `appendUserMessage`: Adds user messages to a conversation
 * 2. `appendStreamResponse`: Handles incoming assistant response chunks
 *
 * These reducers work independently of the thunk's lifecycle (pending/fulfilled/rejected),
 * which is why the stream functions without explicit handling in extraReducers.
 *
 * Key Reducers
 *
 * Message Handling
 * - `appendUserMessage`: Creates or updates a conversation with a user message
 * - `appendStreamResponse`: Appends assistant response chunks to the current message
 * - `clearConversationById`: Clears a specific conversation's messages
 *
 * Conversation Management
 * - `setActiveConversation`: Sets the current active conversation
 * - `setConversationTitle`: Updates a conversation's title
 * - `createNewConversation`: Initializes a new conversation
 *
 * ## useGptStream Hook Documentation
 *
 * ### Overview
 * `useGptStream` is a custom hook that manages GPT chat interactions, including streaming responses and conversation state.
 *
 * ```typescript
 * interface GptStreamParams {
 *   responseId: string;              // Unique conversation identifier
 *   initialPromptData?: string;      // Optional initial message
 *   groupId?: string | null;         // Optional group identifier
 *   clearOnUnmount?: boolean;        // Whether to clear on unmount
 * }
 *
 * useGptStreamReturn {
 *   messages: Message[];            // Current conversation messages
 *   isStreaming: boolean;          // Whether currently streaming
 *   error: string | null;          // Any error state
 *   startStream: (prompt?: string) => void;  // Start streaming function
 *   allConversations: Conversation[];        // All available conversations
 *   handleSetConversationTitle: (title: string) => void;  // Update title
 *   clearConversation: () => void;           // Clear current conversation
 *   activateConversation: () => void;        // Set as active conversation
 * }
 *
/**
 * A reusable React hook for streaming GPT responses and managing conversation metadata.
 * @param {Object} params
 * @param {string} params.responseId - Unique ID for this conversation (uuid).
 * @param {string} [params.initialPromptData=""] - Initial user prompt or empty.
 * @param {string} [params.groupId=null] - Optional grouping (e.g. portfolio ID).
 * @param {boolean} [params.clearOnUnmount=true] - Whether to clear data on unmount.
 */
export function useGptStream({
  responseId,
  initialPromptData = "",
  groupId = null,
  clearOnUnmount = true
}) {
  const dispatch = useDispatch();

  // Grab conversation from Redux
  const conversation = useSelector((state) =>
    selectConversationById(state, responseId)
  );
  const messages = conversation?.messages || [];

  const {
    selectedPortfolioId,
    chatContext
  } = useSelector((state) => state.gptApi);

  // For easy access to *all* conversations in case you need a UI list
  const allConversations = useSelector(selectAllConversations);

  // Local state to handle streaming status and errors
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Start the streaming request by dispatching the thunk.
   * @param {string} [userPrompt] - If you want to override initialPromptData.
   */
  const startStream = useCallback(
    (userPrompt) => {
      const promptData = userPrompt !== undefined ? userPrompt : initialPromptData;

      if (!promptData.trim()) return;

      setIsStreaming(true);
      setError(null);

      // Use portfolio ID as groupId for portfolio conversations
      const effectiveGroupId = selectedPortfolioId || groupId;

      const promise = selectedPortfolioId && chatContext
        ? dispatch(
            streamChatResponse({
              id: responseId,
              groupId: effectiveGroupId,
              portfolio_id: selectedPortfolioId,
              user_prompt: promptData,
              chatContext
            })
          )
        : dispatch(
            fetchGptResponseStream({
              promptData,
              id: responseId,
              groupId: effectiveGroupId
            })
          );

      promise
        .unwrap()
        .then(() => {
          setIsStreaming(false);
        })
        .catch((err) => {
          setIsStreaming(false);
          setError(err);
        });
    },
    [
      dispatch,
      responseId,
      groupId,
      initialPromptData,
      selectedPortfolioId,
      chatContext
    ]
  );

  // Rest of the hook implementation remains the same
  const handleSetConversationTitle = useCallback((newTitle) => {
    dispatch(setConversationTitle({ id: responseId, title: newTitle }));
  }, [dispatch, responseId]);

  const clearConversation = useCallback(() => {
      // Get the groupId from the conversation
      const conversationId = conversation?.groupId;
      dispatch(deleteConversation({
        conversationId,
      }))
      .unwrap()
      .catch((err) => {
        setError(err.message);
        console.error('Failed to delete conversation:', err);
      });
    }, [dispatch, responseId, conversation]);

  const activateConversation = useCallback(() => {
    dispatch(setActiveConversation(responseId));
  }, [dispatch, responseId]);

  useEffect(() => {
    return () => {
      if (clearOnUnmount) {
        dispatch(clearResponse({ id: responseId }));
      }
    };
  }, [dispatch, responseId, clearOnUnmount]);

  return {
    messages,
    isStreaming,
    error,
    startStream,
    allConversations,
    handleSetConversationTitle,
    clearConversation,
    activateConversation
  };
}
